import * as THREE from 'three';

/** 只让本地相机看到自己的身体（他人视角是另一套 layer，M2 再开） */
export const LAYER_SELF = 1;

export interface BodyPose {
  /** 水平移动速度（m/s），用于走路摆动 */
  speed: number;
  seated: boolean;
}

const HIP_STANDING = 0.9;
const HIP_SEATED = 0.52;
/** 髋到眼睛的高度（含头） */
const NECK_HEIGHT = 0.64;

/**
 * 程序化身体。
 *
 * 结构（关键：相机是**脖子**的子节点，不是兄弟节点）：
 *
 *   root            朝向 = 视角偏航
 *   └ hips          在甲板上的高度 + 反摇晃补偿（身体自己找平衡）
 *      ├ torso/arms/legs
 *      └ headAnchor 俯仰 = 视角俯仰（+ 补偿残差）
 *         ├ head（隐藏：自己的头看不见）
 *         └ camera     ← 由 LocalPlayer 挂进来
 *
 * 这样一来「头动身体不动」在结构上就不可能发生：
 * 相机的位置与朝向完全由脖子推导出来。
 */
export class AvatarBody {
  readonly root = new THREE.Group();
  /** 相机要挂到这里 */
  readonly headAnchor = new THREE.Group();

  private readonly hips = new THREE.Group();
  private readonly torso: THREE.Mesh;
  private readonly head: THREE.Mesh;
  private readonly arms: [THREE.Mesh, THREE.Mesh];
  private readonly legs: [THREE.Mesh, THREE.Mesh];

  private seated = true;
  private walkPhase = 0;

  /**
   * 髋部高度。
   *
   * 默认值只用于「还没有真实模型」时的程序化人偶；接上 VRM 后会被
   * `setEyeHeights()` 覆盖成**模型自己的比例**——Q 版角色眼高明显低于
   * 「髋 0.9 + 颈 0.64」这种成年人估算，用默认值会把整个模型顶起来，
   * 结果就是两个人的头不一样高。
   */
  private hipStanding = HIP_STANDING;
  private hipSeated = HIP_SEATED;

  /** 视角俯仰（弧度） */
  private lookPitch = 0;
  /** 地平线锁的补偿残差：pitch / roll */
  private counterPitch = 0;
  private counterRoll = 0;
  private bob = 0;

  constructor() {
    const cloth = new THREE.MeshStandardMaterial({ color: 0x4f6f8f, roughness: 0.9 });
    const trouser = new THREE.MeshStandardMaterial({ color: 0x2c3742, roughness: 0.95 });
    const skin = new THREE.MeshStandardMaterial({ color: 0xc9a07a, roughness: 0.8 });

    this.torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.148, 0.3, 4, 12), cloth);
    this.torso.position.y = 0.26;

    this.head = new THREE.Mesh(new THREE.SphereGeometry(0.115, 16, 12), skin);
    this.head.position.y = 0.02;
    this.head.visible = false; // 自己的头永远看不见，否则低头会看到头颅内壁

    const armGeo = new THREE.CapsuleGeometry(0.065, 0.42, 4, 10);
    this.arms = [new THREE.Mesh(armGeo, cloth), new THREE.Mesh(armGeo, cloth)];
    // 手臂微微前伸：第一人称低头时能看见手，是「有身体」最强的信号
    this.arms[0].position.set(-0.235, 0.2, -0.12);
    this.arms[0].rotation.set(-0.35, 0, 0.12);
    this.arms[1].position.set(0.235, 0.2, -0.12);
    this.arms[1].rotation.set(-0.35, 0, -0.12);

    const legGeo = new THREE.CapsuleGeometry(0.085, 0.62, 4, 10);
    this.legs = [new THREE.Mesh(legGeo, trouser), new THREE.Mesh(legGeo, trouser)];
    this.legs[0].position.set(-0.105, -0.36, 0);
    this.legs[1].position.set(0.105, -0.36, 0);

    this.headAnchor.position.y = NECK_HEIGHT;
    this.headAnchor.rotation.order = 'YXZ';
    this.headAnchor.add(this.head);

    this.hips.position.y = HIP_SEATED;
    this.hips.add(this.torso, ...this.arms, ...this.legs, this.headAnchor);
    this.root.add(this.hips);

    for (const mesh of [this.torso, this.head, ...this.arms, ...this.legs]) {
      mesh.layers.set(LAYER_SELF);
      mesh.frustumCulled = false;
    }

    this.setSeated(true);
    this.applyOrientation();
  }

  get eyeHeight(): number {
    return (this.seated ? this.hipSeated : this.hipStanding) + NECK_HEIGHT;
  }

  /**
   * 用模型的真实比例设置眼高（站 / 坐）。
   * 相机挂在 headAnchor 上，它的世界高度 = 髋高 + 颈高，所以这里反推髋高，
   * 保证「相机位置」= 「模型的眼位」，两个人对得上。
   */
  setEyeHeights(standingEye: number, seatedEye: number): void {
    this.hipStanding = standingEye - NECK_HEIGHT;
    this.hipSeated = seatedEye - NECK_HEIGHT;
    this.hips.position.y = this.seated ? this.hipSeated : this.hipStanding;
  }

  get isSeated(): boolean {
    return this.seated;
  }

  setSeated(seated: boolean): void {
    this.seated = seated;
    this.hips.position.y = seated ? this.hipSeated : this.hipStanding;
    for (const leg of this.legs) {
      leg.rotation.x = seated ? -1.35 : 0;
      leg.position.y = seated ? -0.16 : -0.36;
      leg.position.z = seated ? -0.26 : 0;
    }
    for (const arm of this.arms) {
      arm.rotation.x = seated ? -0.6 : -0.35;
    }
    this.applyOrientation();
  }

  /** 视角俯仰：脖子带着相机一起动 */
  setLook(pitch: number): void {
    this.lookPitch = pitch;
    this.applyOrientation();
  }

  /**
   * 地平线锁的补偿：整个身体在腰部「找平衡」，脖子再补足剩下的量。
   * 真人站在晃动的甲板上就是这么做的——所以画面稳了，身体也不会看起来脱节。
   */
  setCounterTilt(pitch: number, roll: number): void {
    this.counterPitch = pitch;
    this.counterRoll = roll;
    this.applyOrientation();
  }

  private applyOrientation(): void {
    // 腰部只承担一部分，避免整个人像根棍子一样整体倾斜
    this.hips.rotation.set(this.counterPitch * 0.45, 0, this.counterRoll * 0.55);

    // 脖子补足全部补偿，并叠加视角俯仰
    this.headAnchor.rotation.set(this.lookPitch + this.counterPitch, 0, this.counterRoll);
    this.headAnchor.position.y = NECK_HEIGHT + this.bob;
  }

  /** 走路时轻微上下与手臂摆动：让人一眼看出「在走」而不是滑行 */
  update(dt: number, pose: BodyPose): void {
    this.hips.position.y = pose.seated ? this.hipSeated : this.hipStanding;
    if (pose.seated) return;

    const moving = pose.speed > 0.05;
    this.walkPhase += dt * (moving ? pose.speed * 3.4 : 0);
    const swing = moving ? Math.sin(this.walkPhase) * 0.35 : 0;

    this.arms[0].rotation.x = -0.35 - swing * 0.5;
    this.arms[1].rotation.x = -0.35 + swing * 0.5;
    this.legs[0].rotation.x = swing * 0.8;
    this.legs[1].rotation.x = -swing * 0.8;

    // 上下起伏作用在脖子上（相机跟着晃），不是整体平移
    this.bob = moving ? Math.abs(Math.sin(this.walkPhase)) * 0.015 : 0;
    this.headAnchor.position.y = NECK_HEIGHT + this.bob;
  }

  /** 程序化人偶整体显隐（接上真实模型后关掉它，但保留 headAnchor 给相机用） */
  setMeshesVisible(visible: boolean): void {
    for (const mesh of [this.torso, this.head, ...this.arms, ...this.legs]) mesh.visible = visible;
  }

  /** 身体朝向：跟随视角偏航（头再转是脖子的事，M1 不做独立转头） */
  setYaw(yaw: number): void {
    this.root.rotation.y = yaw;
  }

  /** 远端玩家需要完整可见（M2） */
  setSelfOnly(selfOnly: boolean): void {
    const layer = selfOnly ? LAYER_SELF : 0;
    for (const mesh of [this.torso, ...this.arms, ...this.legs]) mesh.layers.set(layer);
  }
}
